import React from "react";
import type { IconNames } from "../Icons/LucideIcons";
import { Icon } from "../Icons/LucideIcons";
import { Body } from "../Typography";
import type { BaseButtonProps } from "./__styles__/BaseButton";
import {
  ButtonContentContainer,
  ButtonContentWrapper,
  LoadingOverlayContainer,
  LoadingWrapper,
  StyledButton,
} from "./__styles__/Button";

/**
 * `Button` extends `BaseButton`, which in turn extends react-aria's `Button`
 *  component. Please note the following:
 *
 * - Loading state is conveyed via the `isPending` prop rather than `loading` as
 *   in previous Button components.
 *
 * - Disabled state is conveyed via the `isDisabled` prop rather than
 *   `disabled`.
 *
 * - You do not need to manually set `isDisabled` when `isPending` is true; the
 *   internal logic will handle this for you to ensure the button remains
 *   accessible while the operation is pending.
 *
 * - Prefer `onPress` over `onClick` for most use cases. See
 *   https://react-spectrum.adobe.com/blog/building-a-button-part-1.html#unified-press-events
 *   for more details.
 *
 * - The `css` and `style` props are explicitly excluded from list of available
 *   props to prevent consumers from overriding internal styles. If you find
 *   yourself needing to override these, please check with design first.
 */

export type ButtonVariant =
  | "primary"
  | "secondary"
  | "critical"
  | "outline"
  | "outlineInverted";

export type ButtonSize = "small" | "medium" | "large";

// this [somewhat] discriminated union prevents incorrect usage of the button
// props; specifically, it requires that a button *either* have a title *or* a
// singular icon; when a title is provided, it optionally allows start and end
// icons
type ContentProps =
  | {
      icon?: never;
      iconStart?: IconNames;
      iconEnd?: IconNames;
      title: string;
      "aria-label"?: string;
    }
  | {
      icon: IconNames;
      iconStart?: never;
      iconEnd?: never;
      title?: never;
      "aria-label": string;
    };

export type ButtonProps = {
  variant?: ButtonVariant;
  size?: ButtonSize;
} & Omit<BaseButtonProps, "css" | "style"> &
  ContentProps;

export function Button({
  variant = "primary",
  size = "medium",
  title,
  iconStart,
  iconEnd,
  icon,
  "aria-label": ariaLabel,
  ...props
}: ButtonProps) {
  const resolvedAriaLabel = ariaLabel ?? title ?? "";
  const contentColor =
    ["primary", "critical", "outlineInverted"].includes(variant) ?
      "contentPrimaryInverted"
    : "contentPrimary";

  const { isPending } = props;

  return (
    <StyledButton
      variant={variant}
      size={size}
      iconOnly={!!icon}
      hasIconStart={!!iconStart}
      hasIconEnd={!!iconEnd}
      aria-label={resolvedAriaLabel}
      {...props}
    >
      <>
        <ButtonContentWrapper isPending={isPending}>
          {icon ?
            <ButtonContentContainer>
              <Icon
                iconName={icon}
                size={16}
                color={contentColor}
                aria-hidden={true}
              />
            </ButtonContentContainer>
          : <ButtonContentContainer>
              {iconStart ?
                <Icon
                  iconName={iconStart}
                  size={16}
                  color={contentColor}
                  aria-hidden={true}
                />
              : null}
              {title ?
                <Body
                  as="span"
                  type="regular"
                  size={size === "small" ? "default" : "large"}
                  color={contentColor}
                >
                  {title}
                </Body>
              : null}
              {iconEnd ?
                <Icon
                  iconName={iconEnd}
                  size={16}
                  color={contentColor}
                  aria-hidden={true}
                />
              : null}
            </ButtonContentContainer>
          }
        </ButtonContentWrapper>
        {isPending ?
          <LoadingOverlayContainer>
            <LoadingWrapper>
              <Icon
                iconName="loading"
                size={16}
                color={contentColor}
                aria-label="Loading"
              />
            </LoadingWrapper>
          </LoadingOverlayContainer>
        : null}
      </>
    </StyledButton>
  );
}
